<template>
  <div class="wt2-add-wallpaper wt2-modal-inner">
    <div class="-title wt2-text-l2" v-text="titleText"></div>
    <div class="-main">
      <div class="-colors-display">
        <div class="-sub-title wt2-text-n2">
          {{ 'wallpaper.brand' | culture }}
        </div>
        <div v-if="brandColors.length" class="-color-list">
          <wallpaper
            :key="i"
            v-for="(c, i) in brandColors"
            class="-brand"
            :color="c"
            :selected="isSelected(c)"
            @select="selectWallpaper(c)"
          ></wallpaper>
        </div>
        <no-wallpapers v-else :count="8">
          <div class="wt2-text-n2">{{ 'wallpaper.empty1' | culture }}</div>
          <div class="wt2-text-s">
            <span class="-contact" @click="contactSupport">{{
              'wallpaper.empty2a' | culture
            }}</span>
            {{ 'wallpaper.empty2b' | culture }}
          </div>
        </no-wallpapers>
        <div class="-sub-title wt2-text-n2">
          {{ 'wallpaper.recent' | culture }}
        </div>
        <div v-if="customWallpapers.length" class="-color-list">
          <wallpaper
            :key="i"
            v-for="(c, i) in customWallpapers"
            class="-custom"
            :color="c"
            :selected="isSelected(c)"
            @select="selectWallpaper(c)"
            @delete="DELETE_WALLPAPER(c)"
          ></wallpaper>
        </div>
        <no-wallpapers v-else :count="10" mode="small">
          <div class="wt2-text-n2">{{ 'wallpaper.empty3' | culture }}</div>
        </no-wallpapers>
      </div>
      <div class="-color-picker">
        <wt-color-picker
          :color-data="selectedWallpaper && selectedWallpaper.wallpaperData"
          v-model="selectedColor"
        />
      </div>
    </div>
    <div class="-actions" v-tab-guard>
      <div v-if="showReplaceCheckbox" class="-replace">
        <wt-check-box
          class="-cb"
          v-model="replaceColorsInTimeline"
        ></wt-check-box>
        Replace all
        <span
          class="-from"
          :style="getWallpaperStyle(asset.wallpaperMetaData.wallpaperData)"
        ></span>
        with
        <span class="-to" :style="getWallpaperStyle(selectedColor)"></span>
      </div>
      <div v-else></div>
      <div class="-buttons">
        <wt-button
          class="-min-width-unset"
          tabindex="0"
          type="transparent"
          :text="'generic.CANCEL' | culture"
          @click="closeModal()"
        />
        <wt-button tabindex="-1" :text="actionText" @click="doAction" />
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';

export default {
  store() {
    return app.rootStore;
  },

  data() {
    return {
      selectedWallpaper: {},
      selectedColor: {},
      replaceColorsInTimeline: true,
      processing: false,
    };
  },

  props: {
    closeModal: {
      type: Function,
      required: true,
    },
    asset: {
      type: Object,
    },

    mode: {
      type: String,
      default: 'add',
      validator(value) {
        return ['add', 'replace'].includes(value);
      },
    },
  },

  analytics: {
    openModal: 'Color Cards Dialog Opened',
    applyCard: 'Color Cards Dialog Applied',
    cardReplace: 'Color Card Replaced',
  },

  computed: Object.assign(
    {
      showReplaceCheckbox() {
        return (
          this.editMode &&
          !this.wallpapersEqual(
            this.asset.wallpaperMetaData.wallpaperData,
            this.selectedColor
          ) &&
          this.isUsed
        );
      },
      editMode() {
        return this.mode === 'replace';
      },
      titleText() {
        const path = this.editMode ? 'generic.EDIT' : 'generic.ADD';
        return app.culture.get(path) + ' ' + app.culture.get('wallpaper.title');
      },

      actionText() {
        const path = this.editMode ? 'generic.REPLACE' : 'generic.ADD';
        return app.culture.get(path);
      },
    },
    app.Vuex.mapState({
      brandColors: (state) => state.wallpapers.brandColors,
      customWallpapers: (state) => state.wallpapers.customWallpapers,
    })
  ),

  watch: {
    selectedColor() {
      if (_.isEmpty(this.selectedWallpaper) || _.isEmpty(this.selectedColor)) {
        return;
      }
      if (
        !this.wallpapersEqual(
          this.selectedWallpaper.wallpaperData,
          this.selectedColor
        )
      ) {
        this.selectWallpaper(null);
      }
    },
  },

  created() {
    this.$triggerAnalytics('openModal', { action: this.mode });
    this.GET_WALLPAPERS().then(() => {
      if (this.asset) {
        const assetColor = this.asset.colorName;
        const wallpaperId = this.asset.wallpaperId;
        if (assetColor) {
          const index = _.findIndex(this.brandColors, { name: assetColor });
          if (index >= 0) {
            this.selectWallpaper(this.brandColors[index]);
          }
        } else if (wallpaperId) {
          const index = _.findIndex(this.customWallpapers, { id: wallpaperId });
          if (index >= 0) {
            this.selectWallpaper(this.customWallpapers[index]);
          }
        }
        return;
      }
      if (!_.isEmpty(this.brandColors)) {
        this.selectWallpaper(this.brandColors[0]);
      } else {
        if (!_.isEmpty(this.customWallpapers)) {
          this.selectWallpaper(this.customWallpapers[0]);
        }
      }
    });
  },

  methods: Object.assign(
    {
      contactSupport() {
        const message = app.culture.get('wallpaper.contactSupport');
        app.intercomController.showNewMessage(message).catch(() => {
          // fallback - if there no intercom, go to website support
          app.openSupportTab();
        });
      },

      selectWallpaper(color) {
        this.selectedWallpaper = color;
      },

      isSelected(color) {
        if (!this.selectedWallpaper) {
          return false;
        }
        if (
          color._source === 'brand' &&
          this.selectedWallpaper._source === 'brand' &&
          color.name === this.selectedWallpaper.name
        ) {
          return true;
        }
        if (
          color._source === 'custom' &&
          this.selectedWallpaper._source === 'custom' &&
          color.id === this.selectedWallpaper.id
        ) {
          return true;
        }
      },

      assetToWallpaper(asset) {
        const wallpaper = {
          wallpaperData: asset.wallpaperMetaData.wallpaperData,
          wallPaperType: asset.wallpaperMetaData.type,
          name: asset.colorName,
        };
        return wallpaper;
      },

      wallpaperToAsset(wallpaper) {
        const caption =
          wallpaper.wallpaperType === 'SOLID'
            ? app.getHexString(wallpaper.wallpaperData.colorValue)
            : 'GRADIENT';
        const id = app.guid();
        const asset = {
          type: 'wallpapermetaasset',
          metadata: {
            assetUUID: id + '_asset_server',
            assetType: 'WALLPAPER',
            providerAssetId: id,
            providerId: 'asset_server',
            caption,
          },
          wallpaperId: wallpaper.id,
          wallpaperMetaData: {
            type: wallpaper.wallpaperType,
            wallpaperData: wallpaper.wallpaperData,
          },
        };

        if (wallpaper._source === 'brand') {
          asset.colorName = wallpaper.name;
        }
        return asset;
      },

      gradientsEqual(w1, w2) {
        if (w1.gradientType !== w2.gradientType) {
          return false;
        }
        if (w1.gradientType === 'LINEAR' && w1.angle !== w2.angle) {
          return false;
        }
        if (w1.gradientColors.length !== w2.gradientColors.length) {
          return false;
        }
        for (let i = 0; i < w1.gradientColors.length; i++) {
          const c1 = w1.gradientColors[i];
          const c2 = w2.gradientColors[i];
          if (!tinycolor.equals(c1.color, c2.color)) {
            return false;
          }
          if (c1.position !== c2.position) {
            return false;
          }
        }

        return true;
      },

      normalizeWallpaperData(wallpaper) {
        const data = wallpaper.wallpaperData;
        if (data.colorValue) {
          data.colorValue = app.getRgbaString(data.colorValue);
        }
        if (data.gradientColors) {
          data.gradientColors = _.map(data.gradientColors, (gc) => {
            return Object.assign(gc, { color: app.getRgbaString(gc.color) });
          });
        }
      },

      wallpapersEqual(w1, w2) {
        if (!!w1.colorValue && !!w2.colorValue) {
          // both solid;
          return tinycolor.equals(w1.colorValue, w2.colorValue);
        }
        if (
          (!!w1.colorValue && !w2.colorValue) ||
          (!w1.colorValue && !!w2.colorValue)
        ) {
          return false; // one solid, on gradient
        }
        return this.gradientsEqual(w1, w2);
      },

      addAssetToLightbox(wallpaper) {
        this.ASSET_SELECTED({ asset: this.wallpaperToAsset(wallpaper) });
      },

      addOrUpdateAsset(wallpaper) {
        if (this.editMode) {
          this.updateSelectedAssetInLightbox(wallpaper);
        } else {
          this.addAssetToLightbox(wallpaper);
        }
      },

      updateSelectedAssetInLightbox(wallpaper) {
        const payload = {
          id: this.asset.id,
          wallpaperId: wallpaper.id,
          wallpaperType: wallpaper.wallpaperType,
          wallpaperData: wallpaper.wallpaperData,
          wallpaperName: wallpaper.name,
        };
        this.UPDATE_SELECTED_ASSET(payload);
      },

      addCustomWallpaper(wallpaper) {
        const identicalWallpaper = _.find(this.customWallpapers, (w) => {
          return this.wallpapersEqual(wallpaper.wallpaperData, w.wallpaperData);
        });
        if (identicalWallpaper) {
          return this.TOUCH_WALLPAPER(identicalWallpaper).then(() => {
            this.addOrUpdateAsset(wallpaper);
          });
        } else {
          return this.ADD_CUSTOM_WALLPAPER(wallpaper).then((wallpaperId) => {
            wallpaper.id = wallpaperId;
            this.addOrUpdateAsset(wallpaper);
          });
        }
      },

      getWallpaperStyle(wallpaperData) {
        return app.computeBgFromColorData(wallpaperData);
      },

      doAction: _.debounce(function () {
        if (this.processing) {
          return;
        }
        // no need to falsify processing later because
        // closeModal destroy the component instance
        this.processing = true;
        let oldId;
        let oldName;
        if (this.editMode && this.asset) {
          oldId = this.asset.wallpaperId;
          oldName = this.asset.colorName;
        }
        const selectedWallpaper = _.cloneDeep(this.selectedWallpaper);
        const isPickerDifferentThanSelected =
          _.isEmpty(selectedWallpaper) ||
          !this.wallpapersEqual(
            selectedWallpaper.wallpaperData,
            this.selectedColor
          );
        if (isPickerDifferentThanSelected) {
          const wallpaperFromPicker = {
            wallpaperType: this.selectedColor.colorValue ? 'SOLID' : 'GRADIENT',
            wallpaperData: this.selectedColor,
          };
          this.normalizeWallpaperData(wallpaperFromPicker);
          this.sendApplyEvent('new', wallpaperFromPicker);
          this.addCustomWallpaper(wallpaperFromPicker).then(() => {
            this._closeModal(wallpaperFromPicker, oldId, oldName);
          });
        } else {
          this.sendApplyEvent(selectedWallpaper._source, selectedWallpaper);
          if (selectedWallpaper._source === 'custom') {
            this.TOUCH_WALLPAPER(selectedWallpaper).then(() => {
              this.addOrUpdateAsset(selectedWallpaper);
              this._closeModal(selectedWallpaper, oldId, oldName);
            });
          }
          if (selectedWallpaper._source === 'brand') {
            this.addCustomWallpaper(selectedWallpaper).then(() => {
              this._closeModal(selectedWallpaper, oldId, oldName);
            });
          }
        }
      }, app.TIME.MENTAL.VISUAL),

      sendApplyEvent(source, wallpaper) {
        let type = 'solid';
        if (wallpaper.wallpaperType === 'GRADIENT') {
          type = 'gradient';
        }
        this.$triggerAnalytics('applyCard', {
          source,
          action: this.mode,
          type,
        });
        if (this.editMode && this.showReplaceCheckbox) {
          this.$triggerAnalytics('cardReplace', {
            replaceInTimeline: this.replaceColorsInTimeline,
          });
        }
      },

      _closeModal(newWallpaper, oldId, oldName) {
        this.closeModal(
          this.editMode && this.replaceColorsInTimeline
            ? { newWallpaper, oldId, oldName }
            : undefined
        );
      },

      onPickerUpdated(newData) {
        const curData = this.selectedWallpaper.wallpaperData;
        if (curData.colorValue === newData.colorValue) {
          return;
        }
        this.selectWallpaper({});
      },
    },
    app.Vuex.mapActions([
      'ADD_CUSTOM_WALLPAPER',
      'TOUCH_WALLPAPER',
      'DELETE_WALLPAPER',
      'ASSET_SELECTED',
      'GET_WALLPAPERS',
      'UPDATE_SELECTED_ASSET',
    ])
  ),

  components: {
    'no-wallpapers': {
      template: `<div class="-color-list">
<div class="-empty-message">
    <slot></slot>
</div>
<div v-for="i in dummyArray" class="-wallpaper -placeholder" :class="'-' + mode"></div>
</div>`,
      props: {
        count: {
          type: Number,
          required: true,
        },
        mode: {
          type: String,
          default: 'large',
        },
      },
      data() {
        return {
          dummyArray: [],
        };
      },
      beforeMount() {
        for (let i = 0; i < this.count; i++) {
          this.dummyArray.push(i);
        }
      },
    },
    wallpaper: {
      template: `<div class="-wallpaper" :class="{'-selected' : selected}">
<div class="-color" @click="select">
    <div class="-inner" :style="getWallpaperStyle(color)"></div>
</div>
<div v-if="!isGradient" class="-hex wt2-text-s" ref="hexLabel" @click="selectText">{{hex}}</div>
</div>
</div>
`,
      props: {
        color: {
          type: Object,
          required: true,
        },
        selected: {
          type: Boolean,
          default: false,
        },
      },

      computed: {
        hex() {
          return app
            .getHexString(this.color.wallpaperData.colorValue)
            .toUpperCase();
        },
        isGradient() {
          return this.color.wallpaperType === 'GRADIENT';
        },
      },

      methods: {
        getWallpaperStyle(color) {
          return app.computeBgFromColorData(color.wallpaperData);
        },

        select(e) {
          if (e && e.metaKey) {
            this.$emit('delete');
          } else {
            this.$emit('select');
            this.selectText();
          }
        },

        selectText() {
          if (this.$refs.hexLabel && window.getSelection) {
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(this.$refs.hexLabel);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        },
      },
    },
  },
};
</script>
